import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ConnectionService } from '../../../../../services/connection-service';
import { UploadFileService } from '../../../../../services/uploadFileService/uploadFile.service';
import { Broadcaster } from '../../../../../components/broadcaster';
import { AppConfig } from '../../../../app.config';

export interface LanguageStringPointer {
  objectId: string;
  className: string;
  __type: string;
}

export interface ExtraImageWithTagPersisted {
  imageLink: string;
  tag: string;
  priority: number;
  titleLanguageString?: LanguageStringPointer;
  contentLanguageString?: LanguageStringPointer;
}

interface ExtraImageWithTagRow {
  imageLink: string;
  tag: string;
  priority: number;
  titleLanguageStringId: string;
  contentLanguageStringId: string;
  titleLanguageStringText: string;
  contentLanguageStringText: string;
  uploading: boolean;
  uploadRequestId: number;
  error: string;
}

@Component({
  selector: 'extra-images-with-tags',
  templateUrl: './extra-images-with-tags.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule],
})
export class ExtraImagesWithTagsComponent implements OnInit {
  @Input() value: Array<ExtraImageWithTagPersisted> = [];

  rows: Array<ExtraImageWithTagRow> = [];

  private readonly MAX_IMAGE_SIZE_BYTES: number = 1_500_000;
  private currentUser: any;

  constructor(
    private readonly conn: ConnectionService,
    private readonly uploadFileService: UploadFileService,
    private readonly broadcasterService: Broadcaster,
    private readonly appConfig: AppConfig,
  ) {}

  async ngOnInit(): Promise<void> {
    this.currentUser = this.conn.getCurrentUser();
    const seed: Array<ExtraImageWithTagPersisted> = Array.isArray(this.value) ? this.value : [];
    this.rows = seed.map((item: ExtraImageWithTagPersisted): ExtraImageWithTagRow => this.createRow(item));
    await Promise.all(this.rows.map((row: ExtraImageWithTagRow): Promise<void> => this.loadLanguageStringTexts(row)));
  }

  addRow(): void {
    const nextPriority: number = this.rows.length
      ? Math.max(0, ...this.rows.map((r: ExtraImageWithTagRow): number => r.priority)) + 1
      : 1;
    this.rows.push(this.createRow({ imageLink: '', tag: '', priority: nextPriority }));
  }

  removeRow(index: number): void {
    this.rows.splice(index, 1);
  }

  newLanguageStringUrl(): string {
    return `${this.conn.getBaseUrl()}/languageStrings/new`;
  }

  languageStringEditUrl(languageStringId: string): string {
    return `${this.conn.getBaseUrl()}/languageString/${(languageStringId || '').trim()}/edit`;
  }

  async uploadImage(files: FileList, row: ExtraImageWithTagRow): Promise<void> {
    const file: File = files?.[0];
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      this.broadcasterService.broadcast('NOTIFY', {
        message: 'Only image files are allowed',
        type: this.appConfig.Shared.Toast.Type.ERROR,
      });
      return;
    }
    if (file.size > this.MAX_IMAGE_SIZE_BYTES) {
      this.broadcasterService.broadcast('NOTIFY', {
        message: 'Image size should be less than 1.5MB',
        type: this.appConfig.Shared.Toast.Type.ERROR,
      });
      return;
    }
    const requestId: number = ++row.uploadRequestId;
    row.uploading = true;
    try {
      const url: string = await this.uploadFileService.uploadFile({
        file,
        bucket: 'PRODUCT',
        source: 'doctor_product',
        username: this.currentUser?.get('username'),
      });
      if (row.uploadRequestId === requestId) {
        row.imageLink = url;
      }
    } catch {
      if (row.uploadRequestId === requestId) {
        this.broadcasterService.broadcast('NOTIFY', {
          message: 'Failed to upload image',
          type: this.appConfig.Shared.Toast.Type.ERROR,
        });
      }
    } finally {
      if (row.uploadRequestId === requestId) {
        row.uploading = false;
      }
    }
  }

  /**
   * Resolves the English text of whichever ids are currently typed in, so the row can show what it points at instead
   * of a bare objectId. An id that resolves to nothing leaves the text empty, which `validate` reports as an error.
   */
  async loadLanguageStringTexts(row: ExtraImageWithTagRow): Promise<void> {
    const [titleText, contentText]: Array<string> = await Promise.all([
      this.fetchLanguageStringText(row.titleLanguageStringId),
      this.fetchLanguageStringText(row.contentLanguageStringId),
    ]);
    row.titleLanguageStringText = titleText;
    row.contentLanguageStringText = contentText;
  }

  async validate(): Promise<boolean> {
    this.rows.forEach((row: ExtraImageWithTagRow): void => { row.error = ''; });

    const filledRows: Array<ExtraImageWithTagRow> = this.rows.filter(
      (r: ExtraImageWithTagRow): boolean => !!r.imageLink || !!(r.tag || '').trim(),
    );

    filledRows.forEach((row: ExtraImageWithTagRow): void => {
      const rowNumber: number = this.rows.indexOf(row) + 1;
      if (!row.imageLink) {
        row.error = `Row ${rowNumber}: image is missing`;
        return;
      }
      if (!(row.tag || '').trim()) {
        row.error = `Row ${rowNumber}: tag is missing`;
      }
    });

    const seenPriorities: Map<number, number> = new Map();
    filledRows.forEach((row: ExtraImageWithTagRow): void => {
      if (row.error) return;
      const rowNumber: number = this.rows.indexOf(row) + 1;
      if (typeof row.priority !== 'number' || Number.isNaN(row.priority) || row.priority <= 0) {
        row.error = `Row ${rowNumber}: priority must be a positive number`;
        return;
      }
      if (seenPriorities.has(row.priority)) {
        const otherRowNumber: number = seenPriorities.get(row.priority);
        row.error = `Row ${rowNumber}: priority ${row.priority} is already used by row ${otherRowNumber}`;
        return;
      }
      seenPriorities.set(row.priority, rowNumber);
    });

    await this.validateLanguageStringIds(filledRows);

    const firstError: ExtraImageWithTagRow = this.rows.find(
      (r: ExtraImageWithTagRow): boolean => !!r.error,
    );
    if (firstError) {
      this.broadcasterService.broadcast('NOTIFY', {
        message: firstError.error,
        type: this.appConfig.Shared.Toast.Type.ERROR,
      });
      return false;
    }
    return true;
  }

  getValue(): Array<ExtraImageWithTagPersisted> {
    return this.rows
      .filter((r: ExtraImageWithTagRow): boolean => !!r.imageLink && !!(r.tag || '').trim())
      .map((r: ExtraImageWithTagRow): ExtraImageWithTagPersisted => {
        const entry: ExtraImageWithTagPersisted = {
          imageLink: r.imageLink,
          tag: r.tag.trim(),
          priority: r.priority,
        };
        const titleId: string = (r.titleLanguageStringId || '').trim();
        const contentId: string = (r.contentLanguageStringId || '').trim();
        if (titleId) entry.titleLanguageString = this.buildPointer(titleId);
        if (contentId) entry.contentLanguageString = this.buildPointer(contentId);
        return entry;
      });
  }

  /**
   * Every typed id must already exist in the LanguageString table — this section never creates one, new strings are
   * added from the LanguageString screen first and then referenced here by id.
   */
  private async validateLanguageStringIds(rows: Array<ExtraImageWithTagRow>): Promise<void> {
    await Promise.all(rows.map(async (row: ExtraImageWithTagRow): Promise<void> => {
      if (row.error) return;
      const rowNumber: number = this.rows.indexOf(row) + 1;
      const fields: Array<{ label: string, id: string }> = [
        { label: 'title', id: (row.titleLanguageStringId || '').trim() },
        { label: 'content', id: (row.contentLanguageStringId || '').trim() },
      ];
      for (const field of fields) {
        if (row.error || !field.id) continue;
        const text: string = await this.fetchLanguageStringText(field.id);
        if (field.label === 'title') row.titleLanguageStringText = text;
        else row.contentLanguageStringText = text;
        if (!text) {
          row.error = `Row ${rowNumber}: ${field.label} language string id "${field.id}" does not exist. `
            + 'Create the language string first, then paste its id here.';
        }
      }
    }));
  }

  private async fetchLanguageStringText(languageStringId: string): Promise<string> {
    const objectId: string = (languageStringId || '').trim();
    if (!objectId) return '';
    try {
      const languageString: any = await this.conn.getLanguageString(objectId);
      return languageString?.get('en') || '';
    } catch {
      return '';
    }
  }

  private buildPointer(objectId: string): LanguageStringPointer {
    return { objectId, className: 'LanguageString', __type: 'Pointer' };
  }

  /**
   * Stored pointers come back either as raw pointer JSON or as a Parse object, depending on how the array field was
   * decoded — read the objectId off either shape.
   */
  private toLanguageStringId(value: any): string {
    return value?.objectId || value?.id || '';
  }

  private createRow(seed: ExtraImageWithTagPersisted): ExtraImageWithTagRow {
    return {
      imageLink: seed.imageLink || '',
      tag: seed.tag || '',
      priority: typeof seed.priority === 'number' ? seed.priority : 0,
      titleLanguageStringId: this.toLanguageStringId(seed.titleLanguageString),
      contentLanguageStringId: this.toLanguageStringId(seed.contentLanguageString),
      titleLanguageStringText: '',
      contentLanguageStringText: '',
      uploading: false,
      uploadRequestId: 0,
      error: '',
    };
  }
}
